import { EmptyState, NotificationsIcon, SectionCard } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';

/**
 * The drawer is honest about the screen being empty.
 *
 * A "How this works" panel on an unbuilt screen is still worth having: the question a
 * client arrives with is "where do I set up notification emails?", and the answer —
 * nowhere yet, because nothing raises one — is more useful from the drawer than from a
 * support ticket. It describes what the screen WILL do and says plainly that it does
 * not do it yet; it does not describe controls that are not there.
 */
const NOTIFICATIONS_HELP: HelpContent = {
  title: 'Notifications',
  icon: '🔔',
  purpose:
    'Will hold the <strong>email and in-app notification settings</strong> for this install &mdash; who is told what, where it is sent, and how often. <strong>It is not built yet</strong>, because nothing in the platform raises a notification to configure.',
  doHere: [
    'Nothing yet. The screen exists so the navigation is complete and so this answer is findable.',
  ],
  fits: 'Administration &rarr; Notifications',
  firstTime:
    'There is nothing to set up here. If a client is asking to be notified about something specific, that is a request for the notification to be <em>raised</em> in the first place, which is work in the module that would raise it &mdash; not configuration on this screen.',
  sections: [
    {
      type: 'html',
      label: 'What it will cover',
      html: 'Three things, once there is something to notify about: the <strong>delivery addresses</strong> a notification goes to, the <strong>digest frequency</strong> (immediately, daily, weekly), and <strong>which events notify whom</strong>. Those are deliberately not stubbed as dead controls &mdash; a toggle that saves a value nothing reads is worse than an empty screen, because it looks configured.',
    },
    {
      type: 'html',
      label: 'What already reaches people without this screen',
      html: 'Two things, and neither is affected by anything here. <strong>Account email</strong> &mdash; an administrator sets or resets a password under <em>Users</em> and communicates it themselves; the platform does not email credentials. And the <strong>audit log</strong>, which records every administrative change and is read on its own screen rather than pushed to anyone.',
    },
  ],
};

/**
 * Notifications — email and in-app notification settings for this install.
 *
 * Not built. Nothing in the platform emits a notification yet, so there is nothing
 * to configure; the screen exists so the navigation is complete and honest.
 */
export function NotificationsTab() {
  return (
    <>
      <HelpPanel help={NOTIFICATIONS_HELP} />
      <SectionCard>
        <EmptyState
          icon={<NotificationsIcon />}
          title="Notification settings are not built yet"
          description="Email and in-app notification preferences for this install — delivery addresses, digest frequency, and which events notify whom. Nothing in the platform raises notifications yet."
        />
      </SectionCard>
    </>
  );
}
